import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {$isImageNode} from 'components/DSYS/editor/nodes/ImageNode';
import {Lightbox} from 'components/DSYS/Lightbox';
import {useEffect, useState} from 'react';

import {isDropboxEmoji} from './EmojiPicker';
import styles from './ImageLightboxPlugin.module.css';

export const ImageLightboxPlugin = () => {
  const [editor] = useLexicalComposerContext();
  const [images, setImages] = useState<string[]>([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(-1);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  // Update images list whenever editor state changes
  useEffect(() => {
    return editor.registerUpdateListener(({editorState}) => {
      editorState.read(() => {
        const imageNodes = [];
        const nodeMap = editor.getEditorState()._nodeMap;
        for (const [, node] of nodeMap) {
          if ($isImageNode(node)) {
            const src = node.getSrc();
            if (!isDropboxEmoji(src)) {
              imageNodes.push(src);
            }
          }
        }
        setImages(imageNodes);
      });
    });
  }, [editor]);

  // Add hover styles to images
  useEffect(() => {
    const addHoverStyles = () => {
      const rootElement = editor.getRootElement();
      if (!rootElement) return;

      const imageElements = rootElement.getElementsByTagName('img');
      Array.from(imageElements).forEach((img) => {
        if (!isDropboxEmoji(img.src) && !editor.isEditable()) {
          img.classList.add(styles.lightboxImage);
        } else {
          img.classList.remove(styles.lightboxImage);
        }
      });
    };

    // Initial setup
    addHoverStyles();

    // Watch for changes in the editor
    const observer = new MutationObserver(addHoverStyles);
    const rootElement = editor.getRootElement();
    if (rootElement) {
      observer.observe(rootElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['src'],
      });
    }

    return () => {
      observer.disconnect();
    };
  }, [editor]);

  // Handle image clicks
  useEffect(() => {
    const handleImageClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.tagName === 'IMG') {
        const src = target.getAttribute('src');
        if (src && !isDropboxEmoji(src)) {
          const index = images.indexOf(src);
          if (index !== -1) {
            setSelectedImageIndex(index);
            setIsLightboxOpen(true);
          }
        }
      }
    };

    const rootElement = editor.getRootElement();
    rootElement?.addEventListener('click', handleImageClick);

    return () => {
      rootElement?.removeEventListener('click', handleImageClick);
    };
  }, [editor, images]);

  return (
    <>
      <Lightbox
        open={isLightboxOpen}
        close={() => setIsLightboxOpen(false)}
        assets={images}
        index={selectedImageIndex}
      />
    </>
  );
};
